import { useEffect, useState } from 'react';
import { Loader2, LogOut, UserCog } from 'lucide-react';
import { Button } from '../ui/button';
import { supportApi } from '../../lib/support';
import { inCustomerAccount, leaveCustomerAccount } from '../../lib/supportSession';
import { useRegion } from '../../hooks/useRegion';
import { getRegionConfig } from '../../lib/regionConfig';

/**
 * Shown on every page while one of us is working inside a customer's
 * account. It says whose account, that changes are logged, and when the
 * session ends, and is the way back out. Nothing renders for anyone else.
 */
export function SupportSessionBanner() {
  const { region } = useRegion();
  const [access, setAccess] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const inside = inCustomerAccount();

  useEffect(() => {
    if (!inside) return;
    supportApi.accessNow().then(setAccess).catch(() => setAccess({ active: false }));
  }, [inside]);

  if (!inside || !access) return null;

  const until = access.expires_at
    ? new Date(access.expires_at).toLocaleTimeString(getRegionConfig(region).dateFormat.locale, {
      hour: 'numeric', minute: '2-digit',
    })
    : null;

  const leave = async () => {
    setLeaving(true);
    await leaveCustomerAccount();
  };

  return (
    <div
      role="status"
      className="mb-6 rounded-cards bg-ochre-soft text-ochre px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="support-session-banner"
    >
      <UserCog className="w-5 h-5 shrink-0 hidden sm:block" aria-hidden="true" />
      <p className="flex-1 min-w-0 text-sm">
        {access.active ? (
          <>
            You&rsquo;re in <span className="font-medium">{access.customer_name}</span>&rsquo;s account for
            onboarding. Changes are logged{until && <>, and this ends at {until}</>}. Billing, Xero,
            Settings and their sign-in are theirs alone.
          </>
        ) : (
          <>Your time in the customer&rsquo;s account has ended.</>
        )}
      </p>
      <Button size="sm" onClick={leave} disabled={leaving} className="shrink-0" data-testid="support-session-leave">
        {leaving ? <Loader2 className="animate-spin" /> : <LogOut />}
        Back to Support
      </Button>
    </div>
  );
}
